import { useEffect, useState } from "react";
import { Smartphone, X, Share, Check, MoreVertical } from "lucide-react";

export default function DownloadAppButton({ className = "", style = {} }) {
  const [deferred, setDeferred] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) { setInstalled(true); return; }

    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      await deferred.userChoice.catch(() => {});
      setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };

  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  if (installed) {
    return (
      <span data-testid="app-installed-badge" className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-bold ${className}`} style={{ color: "#0d8a5f", background: "#e8f8f0", border: "1.5px solid #b5e6cf", ...style }}>
        <Check className="h-4 w-4" /> App Installed
      </span>
    );
  }

  return (
    <>
      <button
        onClick={handleClick}
        data-testid="download-app-btn"
        className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-bold transition-transform hover:-translate-y-0.5 ${className}`}
        style={style}
      >
        <Smartphone className="h-4 w-4" /> Use on Any Device
      </button>

      {showHelp && (
        <div data-testid="download-app-modal" className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: "rgba(13,22,41,0.55)", backdropFilter: "blur(4px)" }} onClick={() => setShowHelp(false)}>
          <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowHelp(false)} data-testid="download-app-modal-close" aria-label="Close" className="absolute right-4 top-4 text-slate-400 hover:text-slate-700">
              <X className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 mb-1">
              <Smartphone className="h-5 w-5" style={{ color: "#0ea5c9" }} />
              <h3 className="text-[18px] font-extrabold text-slate-900">Use Maldek on any device</h3>
            </div>
            <p className="text-[13.5px] text-slate-500 mb-4">Install Maldek from your browser for app-like access — no app store needed, works offline once installed. Native iOS and Android applications are in development.</p>
            {isIOS ? (
              <ol className="space-y-3 text-[14px] text-slate-700">
                <li className="flex gap-3"><span className="font-bold text-slate-400">1.</span><span>Open this site in <b>Safari</b></span></li>
                <li className="flex gap-3"><span className="font-bold text-slate-400">2.</span><span>Tap the <b>Share</b> button <Share className="inline h-4 w-4 -mt-0.5" /> at the bottom</span></li>
                <li className="flex gap-3"><span className="font-bold text-slate-400">3.</span><span>Scroll down and tap <b>Add to Home Screen</b></span></li>
              </ol>
            ) : (
              <ol className="space-y-3 text-[14px] text-slate-700">
                <li className="flex gap-3"><span className="font-bold text-slate-400">1.</span><span>Open this site in <b>Chrome</b> on your phone</span></li>
                <li className="flex gap-3"><span className="font-bold text-slate-400">2.</span><span>Tap the <b>menu</b> <MoreVertical className="inline h-4 w-4 -mt-0.5" /> in the top-right corner</span></li>
                <li className="flex gap-3"><span className="font-bold text-slate-400">3.</span><span>Tap <b>Install app</b> (or <b>Add to Home Screen</b>)</span></li>
              </ol>
            )}
            <p className="mt-4 rounded-lg px-3 py-2 text-[12.5px]" style={{ background: "#eef8fb", color: "#0b6d85" }}>
              The app icon will appear on your home screen and open full-screen, just like a native app.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
